/**
 * The new-planting care page (server: services/bushFollowUp, routes/bush-care.js).
 *
 * The customer opens it from the one message that goes when their bush or shrub installation is finished: how to
 * water a new planting — the first two weeks, weeks three to eight, and once it is established — the maintenance we
 * recommend, and, when the bid carried them, the care-plan prices every 3, 4 or 6 months. Tapping one records it and
 * puts it in front of the office; nothing is charged here.
 *
 * Both schedules are the office's own templates (Settings → Texts & email). This page writes no advice of its own.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { Droplets, Leaf, Check, CalendarClock } from 'lucide-react';
import { publicApi } from '../services/api';
import { companyOf } from '../utils/company';
import { PublicPage, PublicLoading, DeadLink, Panel, H1, OptionRow, ButtonPrimary } from '../components/public';

type Plan = { months: number; price: number; words: string; per_year: number };
type Page = {
  who: string | null; address: string | null; what: string | null; planted_on: string | null;
  watering: { when: string; words: string }[]; maintenance: string;
  plans: Plan[]; chosen: { months: number; price: number; words: string; at: string | null } | null;
};

const money = (n: number) => { const v = Math.round(Number(n) * 100) / 100; return `$${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };
const longDay = (iso: string | null) => (iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' }) : null);

/** A template is plain text: its blank lines are paragraphs and its "- " lines are a list. */
function Words({ text }: { text: string }) {
  const lines = String(text || '').split('\n').map(l => l.trim()).filter(Boolean);
  const bullets = lines.filter(l => /^[-•]\s?/.test(l));
  const plain = lines.filter(l => !/^[-•]\s?/.test(l));
  return (
    <>
      {plain.map((p, i) => <p key={`p${i}`} className="text-[15px] text-gray-800 leading-relaxed mt-1">{p}</p>)}
      {bullets.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {bullets.map((b, i) => (
            <li key={`b${i}`} className="flex items-start gap-2 text-[15px] text-gray-800">
              <Check className="h-4 w-4 mt-1 text-emerald-600 flex-shrink-0" aria-hidden />
              <span>{b.replace(/^[-•]\s?/, '')}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function BushCarePublic() {
  const { token } = useParams<{ token: string }>();
  const [picked, setPicked] = useState<number | null>(null);
  const [thanks, setThanks] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<Page>(
    ['bush-care', token],
    () => publicApi.get(`/bush-care/${token}`).then(r => r.data),
    { enabled: !!token, retry: false },
  );
  const company = companyOf(data);

  const take = useMutation((months: number) => publicApi.post(`/bush-care/${token}/plan`, { months }).then(r => r.data), {
    onSuccess: (d: any) => { setThanks(d?.message || 'Thank you — we have it.'); refetch(); },
    onError: (e: any) => { setThanks(e?.response?.data?.message || 'Could not save that — please text us.'); },
  });

  if (isLoading) return <PublicLoading />;
  if (isError || !data) return <DeadLink what="care page" company={company} />;

  const chosen = data.chosen;

  return (
    <PublicPage company={company} context="Your new plants">
      <section className="bg-white rounded-3xl border border-brand-100 shadow-lift p-5">
        <p className="text-[13px] font-black uppercase tracking-wide text-emerald-700">New planting care</p>
        <H1 className="mt-1">{data.who ? `${data.who}, your new plants are in` : 'Your new plants are in'}</H1>
        {(data.what || data.address) && (
          <p className="mt-2 text-[15px] text-gray-800">
            {data.what}{data.what && data.address ? ' · ' : ''}{data.address}
          </p>
        )}
        {data.planted_on && <p className="mt-1 text-[14px] text-gray-600">Planted {longDay(data.planted_on)}</p>}
        <p className="mt-3 text-[15px] text-gray-800 leading-relaxed">
          The next eight weeks decide whether they take. Nearly everything that goes wrong with a new planting is water —
          too little at first, too much later. Here is exactly what to do.
        </p>
      </section>

      <Panel>
        <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900">
          <Droplets className="h-5 w-5 text-sky-600" aria-hidden /> Watering
        </p>
        <ol className="mt-2 space-y-4">
          {data.watering.map(w => (
            <li key={w.when}>
              <p className="text-[15px] font-bold text-gray-900">{w.when}</p>
              <Words text={w.words} />
            </li>
          ))}
        </ol>
      </Panel>

      <Panel>
        <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900">
          <CalendarClock className="h-5 w-5 text-brand-600" aria-hidden /> Keeping them right
        </p>
        <Words text={data.maintenance} />
      </Panel>

      {chosen && (
        <Panel tone="brand">
          <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900">
            <Check className="h-5 w-5 text-emerald-600" aria-hidden /> You're on the care plan
          </p>
          <p className="mt-2 text-[15px] text-gray-900">
            We come {chosen.words} at {money(chosen.price)} a visit. We'll be in touch with your first visit — nothing is charged today.
          </p>
        </Panel>
      )}

      {!chosen && data.plans.length > 0 && (
        <Panel>
          <p className="flex items-center gap-2 text-[15px] font-black uppercase tracking-tight text-gray-900">
            <Leaf className="h-5 w-5 text-emerald-600" aria-hidden /> Want us to keep them right?
          </p>
          <p className="mt-2 text-[15px] text-gray-800 leading-relaxed">
            We come out, shape them, feed them, top up the mulch and check the water. Pick how often — nothing is charged
            now, and you can change or stop it any time.
          </p>
          <ul className="mt-3 space-y-2">
            {data.plans.map(p => (
              <li key={p.months}>
                <OptionRow on={picked === p.months} ariaLabel={`Every ${p.months} months`} onClick={() => setPicked(p.months)}>
                  <span className={`h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${picked === p.months ? 'border-emerald-600 bg-white' : 'border-brand-300'}`}>
                    {picked === p.months && <span className="h-3 w-3 rounded-full bg-emerald-600" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-bold text-gray-900">We come {p.words}</span>
                    <span className="block text-[13px] text-gray-600">{money(p.per_year)} a year</span>
                  </span>
                  <span className="text-[15px] font-bold text-gray-900 tabular-nums whitespace-nowrap">{money(p.price)}<span className="text-xs font-semibold text-gray-500">/visit</span></span>
                </OptionRow>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <ButtonPrimary disabled={!picked || take.isLoading} onClick={() => picked && take.mutate(picked)}>
              {take.isLoading ? 'Saving…' : picked ? `Start the ${data.plans.find(p => p.months === picked)?.words} plan` : 'Pick one above'}
            </ButtonPrimary>
          </div>
          {thanks && <p className="mt-2 text-[14px] text-gray-800">{thanks}</p>}
        </Panel>
      )}

      <p className="text-center text-[14px] text-gray-600">
        Anything looking unhappy? Text us a photo{company?.phone ? ` at ${company.phone}` : ''} and we'll come look.
      </p>
    </PublicPage>
  );
}
